/**
 * The spec, said out loud.
 *
 * Every result gets a heading that states the question it answers, so a
 * screenshot is self-describing and nobody has to reconstruct the filters from
 * a table of numbers. It is derived from the spec on every render rather than
 * stored, which is the point: an authored title goes stale the moment someone
 * changes a filter, and a stale title is worse than none.
 *
 * The English comes from the catalog, not from here. Properties carry a
 * `phrase` ("received by {value}", "in {value}"), measures carry a verb
 * ("How many", "Share"), events carry a noun ("a progressive pass"), and the
 * subject carries its own plural. This module only decides the word order. Add
 * a property to the catalog, give it a phrase, and the heading picks it up —
 * nobody has to come back and edit this file.
 */
import type { SubjectCatalog } from './catalog'
import { eventBy, measureBy, operatorBy, propertyBy, required } from './catalog'
import type {
  AnalysisSpec, Condition, KnownStep, Measure, SequenceStep,
} from './spec'
import { conditions, isComparison, isPropertyOperand, isSequence, stepsIn } from './spec'

const list = (parts: string[]): string =>
  parts.length <= 1 ? (parts[0] ?? '')
    : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`

const value = (v: unknown): string => {
  if (Array.isArray(v)) return list(v.map(value))
  if (v === null || v === undefined) return ''
  return String(v)
}

/** A filter, in the property's own words where the catalog gives them. */
function comparison(cat: SubjectCatalog, c: Condition): string {
  if (!isComparison(c)) return ''
  const prop = propertyBy(cat, c.property)
  if (!prop) return ''
  const op = operatorBy(prop, c.op)
  const said = value(c.value)

  // `phrase` is the property's preferred wording and wins when it fits: the
  // catalog says "received by 16", which is better English than anything
  // assembled from a label and an operator name.
  if (prop.phrase && (op?.arity === 'one' || op?.arity === 'many')) {
    if (!prop.phrase.includes('{op}')) return prop.phrase.replace('{value}', said)
    return prop.phrase.replace('{op}', op.label).replace('{value}', said)
  }
  if (op?.arity === 'none') return `${prop.label.toLowerCase()} ${op.label}`
  return `${prop.label.toLowerCase()} ${op?.label ?? c.op} ${said}`.trim()
}

/** "a progressive pass within 5 seconds", from the event's own noun. */
function step(cat: SubjectCatalog, s: SequenceStep): string {
  const ev = eventBy(cat, s.event)
  const unit = cat.time_units?.find((u) => u.name === s.within.unit)
  const noun = ev?.phrase ?? ev?.label?.toLowerCase() ?? s.event
  return `${noun} within ${s.within.value} ${unit?.label ?? s.within.unit}`
}

const sequence = (cat: SubjectCatalog, c: Condition): string =>
  isSequence(c) ? `followed by ${list(c.sequence.steps.map((s) => step(cat, s)))}` : ''

const clause = (cat: SubjectCatalog, c: Condition): string =>
  isSequence(c) ? sequence(cat, c) : comparison(cat, c)

/** What a measure asks for: "how many receptions", "what share of them…". */
function asks(
  cat: SubjectCatalog, m: Measure, plural: string, steps: KnownStep[],
): string {
  const fn = measureBy(cat, m.fn)
  const verb = (fn?.label ?? m.fn).toLowerCase()
  if (fn?.operand === 'none') return `${verb} ${plural}`
  if (fn?.operand === 'condition') {
    const of = m.of && !isPropertyOperand(m.of) ? clause(cat, m.of as Condition) : ''
    return of ? `what ${verb} of ${plural} were ${of}` : `what ${verb} of ${plural}`
  }
  const of = m.of
  if (!isPropertyOperand(of)) return verb
  // The operand may name a property of a *step's event* rather than of the
  // subject — the forward progress of the pass that followed, not of the
  // reception. The step says which event, and the event carries the label.
  const ev = of.step
    ? eventBy(cat, steps.find((s) => s.id === of.step)?.event ?? '')
    : undefined
  const named = ev
    ? ev.properties.find((p) => p.name === of.property)
    : propertyBy(cat, of.property)
  // The authored label is the analyst's own words and beats anything assembled
  // here; the catalog label is the fallback when a measure is unlabelled.
  return `the ${verb} ${(m.label ?? named?.label ?? of.property).toLowerCase()}`
}

/**
 * How the results are split up. Two pitch axes binned together is a zone map
 * rather than two independent breakdowns, and saying "in what zones" is both
 * shorter and closer to what the analyst actually asked for.
 */
function splits(cat: SubjectCatalog, spec: AnalysisSpec): string {
  const breaks = spec.breakdowns ?? []
  if (!breaks.length) return ''
  const axes = breaks
    .map((b) => (b.property ? propertyBy(cat, b.property) : undefined))
    .filter((p) => p?.spatial_axis)
  if (axes.length === 2 && breaks.length === 2) {
    const w = breaks[0]?.bins?.width
    return w ? `in what ${w}m zones of the pitch` : 'in what zones of the pitch'
  }
  const said = breaks.map((b) => {
    const prop = b.property ? propertyBy(cat, b.property) : undefined
    const name = b.label ?? prop?.label.toLowerCase() ?? b.property ?? 'group'
    return b.bins?.width
      ? `${name} in ${b.bins.width}${b.bins.unit ? ` ${b.bins.unit}` : ''} bands`
      : name
  })
  return `by ${list(said)}`
}

/**
 * The whole question, ending in a question mark.
 *
 * Deliberately not perfect prose: it is assembled from parts the catalog owns,
 * so it stays true when the catalog grows. A sentence that is slightly stiff
 * but always accurate beats a fluent one that quietly stops matching the spec.
 */
export function questionFor(cat: SubjectCatalog, spec: AnalysisSpec): string {
  const plural = cat.subject?.plural ?? 'subjects'
  const measures = spec.measures ?? []
  const steps = stepsIn(spec)
  // Only the first measure names the subject; after that it is "them", or the
  // heading reads "how many receptions and what share of receptions ...".
  const asked = measures.length
    ? list(measures.map((m, i) => asks(cat, m, i ? 'them' : plural, steps)))
    : `how many ${plural}`

  // The required property is the scope the whole question sits inside — the
  // match — and English puts that last: "received by 16, in SKC/135", never
  // "in SKC/135 and received by 16". Which property that is comes from the
  // catalog, so a subject scoped by something else reads correctly too.
  const scope = new Set(required(cat).map((p) => p.name))
  const all = conditions(spec)
  const ordered = [
    ...all.filter((c) => !(isComparison(c) && scope.has(c.property))),
    ...all.filter((c) => isComparison(c) && scope.has(c.property)),
  ]
  const filters = ordered.map((c) => clause(cat, c)).filter(Boolean)
  const where = splits(cat, spec)

  // "In what zones" leads when there is one, because it is the shape of the
  // answer; otherwise the measure does.
  const head = where.startsWith('in what')
    ? `${where}, ${asked}`
    : [asked, where].filter(Boolean).join(', ')
  // Commas, not "and": these are stacked restrictions, and "and" reads as if
  // the last one were somehow joined to the measure.
  const full = [head, ...filters].filter(Boolean).join(', ')
  return `${full.charAt(0).toUpperCase()}${full.slice(1)}?`
}
